import React, { useEffect, useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, StarIcon, HeadphonesIcon, ArrowRightIcon, SparklesIcon } from 'lucide-react';
interface HeroSectionProps {
  onRentNow?: () => void;
}
export function HeroSection({
  onRentNow
}: HeroSectionProps) {
  const slides = [{
    headline: 'Wheelchair & Scooter Rentals',
    subheadline: 'We deliver to your doorstep with discounted daily/weekly rate'
  }, {
    headline: 'Move With Ease And Comfort',
    subheadline: 'Share blissful memories with your loved ones with our rented wheelchairs and scooters'
  }, {
    headline: 'We Provide Free Delivery',
    subheadline: 'Wheel Around Rentals provides free delivery of rented scooters, wheelchairs at no cost to you'
  }];
  const [currentSlide, setCurrentSlide] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);
  return <section id="home" className="relative min-h-[80vh] flex items-center bg-brand-navy overflow-hidden" data-id="element-164">
      {/* Dot Grid */}
      <div className="absolute inset-0 z-0 opacity-[0.03]" style={{
      backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)',
      backgroundSize: '24px 24px'
    }} data-id="element-165" />

      {/* Gradient orbs */}
      <motion.div animate={{
      y: [0, -20, 0],
      opacity: [0.15, 0.25, 0.15]
    }} transition={{
      duration: 8,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute top-1/3 left-[15%] w-72 h-72 rounded-full bg-brand-blue blur-[120px] z-0 pointer-events-none" data-id="element-166" />
      <motion.div animate={{
      y: [0, 15, 0],
      opacity: [0.1, 0.2, 0.1]
    }} transition={{
      duration: 10,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 2
    }} className="absolute bottom-1/4 right-[20%] w-60 h-60 rounded-full bg-brand-red blur-[100px] z-0 pointer-events-none" data-id="element-167" />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 w-full relative z-10 pt-24 sm:pt-28 pb-24" data-id="element-168">
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-8" data-id="element-169">
          {/* Left Side — Text Content */}
          <div className="flex-1 w-full lg:max-w-2xl" data-id="element-170">
            <div className="h-[280px] sm:h-[260px] md:h-[260px] flex flex-col justify-center relative w-[85%] sm:w-full" data-id="element-171">
              <AnimatePresence mode="wait" data-id="element-172">
                <motion.div key={currentSlide} initial={{
                opacity: 0,
                y: 25
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -25
              }} transition={{
                duration: 0.5,
                ease: [0.22, 1, 0.36, 1]
              }} className="absolute inset-0 flex flex-col justify-center" data-id="element-173">
                  <h1 className="text-3xl sm:text-5xl lg:text-7xl font-extrabold text-white leading-[1.1] tracking-tight mb-4 sm:mb-5" data-id="element-174">
                    {slides[currentSlide].headline}
                  </h1>
                  <motion.p initial={{
                  opacity: 0,
                  y: 15
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.5,
                  delay: 0.15,
                  ease: [0.22, 1, 0.36, 1]
                }} className="text-lg sm:text-xl text-white/75 max-w-lg leading-relaxed" data-id="element-175">
                    {slides[currentSlide].subheadline}
                  </motion.p>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* CTAs */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4,
            duration: 0.6
          }} className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4" data-id="element-176">
              <a href="#reserve" className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-brand-red text-white rounded-full font-bold text-base sm:text-lg hover:bg-red-600 transition-all shadow-[0_0_20px_rgba(230,57,70,0.3)] hover:shadow-[0_0_30px_rgba(230,57,70,0.5)] hover:-translate-y-1 text-center" data-id="element-177">
                Reserve Now
              </a>
              <a href="tel:+18003954459" className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-transparent border-2 border-white/60 text-white rounded-full font-bold text-base sm:text-lg hover:bg-white hover:text-brand-navy transition-all text-center" data-id="element-178">
                Call Us: (800) 395-4459
              </a>
            </motion.div>

            {/* Progress Bar Indicators */}
            <div className="flex gap-2.5 mt-8 sm:mt-10 items-center" data-id="element-179">
              {slides.map((_, index) => <button key={index} onClick={() => setCurrentSlide(index)} className="relative h-1 rounded-full overflow-hidden transition-all duration-300 bg-white/20" style={{
              width: currentSlide === index ? '48px' : '20px'
            }} aria-label={`Go to slide ${index + 1}`} data-id="element-180">
                  {currentSlide === index && <motion.div initial={{
                width: '0%'
              }} animate={{
                width: '100%'
              }} transition={{
                duration: 5,
                ease: 'linear'
              }} className="absolute top-0 left-0 bottom-0 bg-brand-red rounded-full" data-id="element-181" />}
                </button>)}
            </div>

            {/* Mobile Scooter Card */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.6,
            duration: 0.5
          }} className="lg:hidden mt-8" data-id="element-182">
              <div className="rounded-2xl backdrop-blur-xl bg-white/[0.07] border border-white/[0.12] shadow-lg overflow-hidden" data-id="element-183">
                <div className="flex items-center gap-4 p-4" data-id="element-184">
                  <div className="relative shrink-0" data-id="element-185">
                    <img src="https://cdn.magicpatterns.com/uploads/vFPFQp8zQoU1RG9bZBkQpB/scooter-rfwu7jdi9ugdjjqqi1u1fo1vyfphtbhnxy7k3286bk-removebg-preview.png" alt="Heavy-Duty Mobility Scooter" className="w-24 h-24 object-contain drop-shadow-[0_8px_16px_rgba(0,0,0,0.3)]" data-id="element-186" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-187">
                    <div className="flex items-center gap-2 mb-1" data-id="element-188">
                      <h3 className="text-white font-bold text-sm" data-id="element-189">
                        Heavy-Duty Scooter
                      </h3>
                      <span className="bg-emerald-500 text-white px-2.5 py-0.5 rounded-full text-[9px] font-bold whitespace-nowrap" data-id="element-190">
                        Free Delivery
                      </span>
                    </div>
                    <p className="text-white/45 text-xs mb-2.5" data-id="element-191">
                      3 or 4 wheel · All terrain
                    </p>
                    <div className="flex items-center gap-3" data-id="element-192">
                      <div className="bg-brand-red text-white px-2.5 py-1 rounded-lg" data-id="element-193">
                        <span className="text-[9px] uppercase font-semibold opacity-80" data-id="element-194">
                          From{' '}
                        </span>
                        <span className="text-sm font-extrabold" data-id="element-195">$45</span>
                        <span className="text-[9px] font-semibold opacity-80" data-id="element-196">
                          /day
                        </span>
                      </div>
                      <button onClick={onRentNow} className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-white text-brand-navy rounded-lg font-bold text-xs cursor-pointer" data-id="element-197">
                        <SparklesIcon className="w-3 h-3" data-id="element-198" />
                        Rent Now
                        <ArrowRightIcon className="w-3 h-3" data-id="element-199" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right Side — Scooter Product Showcase */}
          <motion.div initial={{
          opacity: 0,
          x: 40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          delay: 0.3,
          ease: [0.22, 1, 0.36, 1]
        }} className="flex-1 hidden lg:flex items-center justify-center relative" data-id="element-200">
            {/* Glow behind card */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-201">
              <motion.div animate={{
              scale: [1, 1.06, 1],
              opacity: [0.12, 0.22, 0.12]
            }} transition={{
              duration: 5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="w-[420px] h-[420px] rounded-full bg-brand-red/15 blur-[80px]" data-id="element-202" />
            </div>

            {/* Product Card */}
            <motion.div animate={{
            y: [0, -6, 0]
          }} transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="relative z-10 w-[360px]" data-id="element-203">
              <div className="rounded-3xl backdrop-blur-xl bg-white/[0.07] border border-white/[0.12] shadow-[0_20px_60px_rgba(0,0,0,0.3)] overflow-hidden" data-id="element-204">
                {/* Image Area */}
                <div className="relative px-8 pt-10 pb-4 flex items-center justify-center" data-id="element-205">
                  {/* Free Delivery badge */}
                  <motion.div initial={{
                  opacity: 0,
                  x: -10
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: 1,
                  duration: 0.4
                }} className="absolute top-4 left-4 z-10" data-id="element-206">
                    <span className="bg-emerald-500 text-white px-3 py-1 rounded-full text-[11px] font-bold shadow-md flex items-center gap-1" data-id="element-207">
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-208">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" data-id="element-209" />
                      </svg>
                      Free Delivery
                    </span>
                  </motion.div>

                  {/* Price Badge */}
                  <motion.div initial={{
                  opacity: 0,
                  scale: 0.8
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  delay: 0.7,
                  duration: 0.4
                }} className="absolute top-4 right-4 z-10" data-id="element-210">
                    <div className="bg-brand-red text-white px-4 py-2 rounded-xl shadow-[0_6px_20px_rgba(230,57,70,0.4)]" data-id="element-211">
                      <div className="text-[10px] font-semibold uppercase tracking-wider opacity-80 leading-none" data-id="element-212">
                        From
                      </div>
                      <div className="text-xl font-extrabold leading-tight" data-id="element-213">
                        $45
                        <span className="text-xs font-semibold opacity-80" data-id="element-214">
                          /day
                        </span>
                      </div>
                    </div>
                  </motion.div>

                  <img src="https://cdn.magicpatterns.com/uploads/vFPFQp8zQoU1RG9bZBkQpB/scooter-rfwu7jdi9ugdjjqqi1u1fo1vyfphtbhnxy7k3286bk-removebg-preview.png" alt="Heavy-Duty Mobility Scooter" className="w-[280px] h-auto drop-shadow-[0_15px_30px_rgba(0,0,0,0.3)]" data-id="element-215" />
                </div>

                {/* Info Area */}
                <div className="px-6 pb-6 pt-2" data-id="element-216">
                  <h3 className="text-white font-bold text-lg mb-1" data-id="element-217">
                    Heavy-Duty Scooter
                  </h3>
                  <p className="text-white/50 text-sm mb-5" data-id="element-218">
                    3 or 4 wheel · Comfortable seat · All terrain
                  </p>

                  <button onClick={onRentNow} className="group w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-white text-brand-navy rounded-xl font-bold text-sm shadow-[0_4px_20px_rgba(0,0,0,0.15)] hover:bg-brand-red hover:text-white hover:shadow-[0_4px_20px_rgba(230,57,70,0.4)] transition-all cursor-pointer" data-id="element-219">
                    <SparklesIcon className="w-4 h-4" data-id="element-220" />
                    Rent This Scooter
                    <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-221" />
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Down Indicator */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 2,
      duration: 1
    }} className="absolute bottom-14 left-1/2 transform -translate-x-1/2 z-20" data-id="element-222">
        <motion.div animate={{
        y: [0, 6, 0]
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-223">
          <ChevronDownIcon className="text-white/40 h-6 w-6" data-id="element-224" />
        </motion.div>
      </motion.div>

      {/* Trust Strip */}
      <div className="absolute bottom-0 left-0 right-0 bg-brand-navy/90 backdrop-blur-sm border-t border-white/5 py-3 z-30" data-id="element-225">
        <div className="max-w-7xl mx-auto px-4 text-center" data-id="element-226">
          <p className="text-white/70 text-sm font-medium tracking-wide" data-id="element-227">
            We are open <span className="text-brand-red font-bold" data-id="element-228">24hrs</span>{' '}
            a day, <span className="text-brand-red font-bold" data-id="element-229">7 days</span> a
            week, <span className="text-brand-red font-bold" data-id="element-230">365 days</span> a
            year
          </p>
        </div>
      </div>
    </section>;
}